import { useState } from 'react';
import { costCalculatorPairs } from '@/mocks/forexTrading';

type AccountType = 'standard' | 'ecn';

const ACCOUNTS: { key: AccountType; label: string }[] = [
  { key: 'standard', label: 'Standard' },
  { key: 'ecn', label: 'ECN' },
];

const MIN_LOTS = 0.01;
const MAX_LOTS = 50;

function formatUsd(value: number) {
  return value.toFixed(2);
}

export default function CostCalculator() {
  const [symbol, setSymbol] = useState(costCalculatorPairs[0].symbol);
  const [account, setAccount] = useState<AccountType>('standard');
  const [lots, setLots] = useState('1');

  const pair = costCalculatorPairs.find((item) => item.symbol === symbol) ?? costCalculatorPairs[0];
  const parsedLots = Number(lots);
  const lotsValue =
    Number.isFinite(parsedLots) && parsedLots > 0 ? Math.min(Math.max(parsedLots, MIN_LOTS), MAX_LOTS) : 0;

  const spreadPerLot = account === 'standard' ? pair.standardPerLot : pair.ecnSpreadPerLot;
  const commissionPerLot = account === 'standard' ? 0 : pair.commissionPerLot;

  const spreadCost = spreadPerLot * lotsValue;
  const commissionCost = commissionPerLot * lotsValue;
  const totalCost = spreadCost + commissionCost;

  const totalPerLot = spreadPerLot + commissionPerLot;
  const spreadShare = totalPerLot > 0 ? (spreadPerLot / totalPerLot) * 100 : 100;

  return (
    <div className="border-t border-background-200 bg-background-100/50 px-5 py-5">
      <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
        <h4 className="flex items-center gap-2 font-heading text-sm font-semibold text-foreground-950">
          <span className="flex h-7 w-7 items-center justify-center rounded-md bg-primary-100 text-primary-700">
            <i className="ri-calculator-line text-sm" />
          </span>
          ลองคำนวณออเดอร์ของคุณ
        </h4>
        <p className="text-xs text-foreground-500">ใช้ตัวเลขชุดเดียวกับตาราง · ยังไม่รวม Swap และ Slippage</p>
      </div>

      <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,1fr)_auto] lg:items-start lg:gap-6">
        <div className="min-w-0 space-y-4">
          <div>
            <p className="text-xs font-medium text-foreground-600">คู่เงิน</p>
            <div className="mt-1.5 flex flex-wrap gap-2">
              {costCalculatorPairs.map((item) => {
                const active = item.symbol === pair.symbol;
                return (
                  <button
                    key={item.symbol}
                    type="button"
                    onClick={() => setSymbol(item.symbol)}
                    aria-pressed={active}
                    className={`cursor-pointer whitespace-nowrap rounded-full border px-3 py-1.5 font-num text-xs font-medium transition-colors duration-200 ${
                      active
                        ? 'border-primary-300 bg-primary-500 text-background-50'
                        : 'border-background-300 bg-background-50 text-foreground-700 hover:border-primary-300 hover:text-primary-700'
                    }`}
                  >
                    {item.symbol}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="flex flex-wrap items-end gap-4">
            <div>
              <p className="text-xs font-medium text-foreground-600">ประเภทบัญชี</p>
              <div className="mt-1.5 inline-flex rounded-full border border-background-300 bg-background-50 p-1">
                {ACCOUNTS.map((item) => {
                  const active = item.key === account;
                  return (
                    <button
                      key={item.key}
                      type="button"
                      onClick={() => setAccount(item.key)}
                      aria-pressed={active}
                      className={`cursor-pointer whitespace-nowrap rounded-full px-3.5 py-1 text-xs font-medium transition-colors duration-200 ${
                        active
                          ? 'bg-secondary-500 text-background-50'
                          : 'text-foreground-600 hover:text-foreground-950'
                      }`}
                    >
                      {item.label}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="min-w-0">
              <label htmlFor="cost-lots" className="text-xs font-medium text-foreground-600">
                ขนาดล็อต
              </label>
              <div className="mt-1.5 flex w-36 items-center gap-2 rounded-md border border-background-300 bg-background-50 px-3 py-2 focus-within:border-primary-400 focus-within:ring-2 focus-within:ring-primary-100">
                <input
                  id="cost-lots"
                  type="number"
                  min="0.01"
                  max="50"
                  step="0.01"
                  inputMode="decimal"
                  value={lots}
                  onChange={(event) => setLots(event.target.value)}
                  className="font-num w-full min-w-0 bg-transparent text-sm font-semibold text-foreground-950 outline-none"
                />
                <span className="shrink-0 text-xs text-foreground-500">lot</span>
              </div>
            </div>
          </div>

          <div>
            <div className="flex h-1.5 w-full overflow-hidden rounded-full bg-background-200/80">
              <div className="h-full bg-accent-500" style={{ width: `${spreadShare}%` }} />
              <div className="h-full bg-secondary-500" style={{ width: `${100 - spreadShare}%` }} />
            </div>
            <p className="mt-2 text-[11px] leading-relaxed text-foreground-500">
              {account === 'ecn'
                ? 'บัญชี ECN แยกเก็บค่าคอมมิชชันออกจาก Spread ทุกครั้งที่เปิดและปิด'
                : 'บัญชี Standard คิดต้นทุนทั้งหมดไว้ใน Spread แล้ว ไม่มีค่าคอมมิชชันแยก'}
            </p>
          </div>
        </div>

        <div className="w-full rounded-lg border border-primary-200 bg-primary-50 px-5 py-4 lg:w-64">
          <p className="text-xs font-medium text-primary-700">ต้นทุนออเดอร์นี้ (ไป-กลับ)</p>
          <p className="mt-1 flex items-end gap-1.5">
            <span className="font-num text-3xl font-semibold leading-none text-primary-800">
              {formatUsd(totalCost)}
            </span>
            <span className="pb-1 text-sm text-primary-700">USD</span>
          </p>

          <dl className="mt-3 space-y-1.5 border-t border-primary-200 pt-2.5">
            <div className="flex items-center justify-between gap-2 text-xs">
              <dt className="text-foreground-600">Spread</dt>
              <dd className="font-num font-semibold text-foreground-950">{formatUsd(spreadCost)} USD</dd>
            </div>
            <div className="flex items-center justify-between gap-2 text-xs">
              <dt className="text-foreground-600">คอมมิชชัน</dt>
              <dd className="font-num font-semibold text-foreground-950">{formatUsd(commissionCost)} USD</dd>
            </div>
          </dl>

          <p className="mt-2.5 border-t border-primary-200 pt-2.5 text-[11px] leading-relaxed text-primary-700">
            {lotsValue > 0
              ? `${lotsValue.toFixed(2)} lot · ต้นทุนต่อล็อต ${formatUsd(totalPerLot)} USD`
              : 'ใส่ขนาดล็อตอย่างน้อย 0.01 เพื่อดูต้นทุน'}
          </p>
        </div>
      </div>
    </div>
  );
}